import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "./Logo";
import { Download, CreditCard, CheckCircle, Loader2, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicInvoice = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [razorpayLoading, setRazorpayLoading] = useState(false);

  useEffect(() => { fetchInvoice(); }, [token]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const paymentParam = searchParams.get("payment");
    if (sessionId && paymentParam === "success") {
      setPaymentStatus("checking");
      pollPaymentStatus(sessionId, 0);
    } else if (paymentParam === "cancelled") {
      setPaymentStatus(null);
    }
  }, [searchParams]);

  const fetchInvoice = async () => {
    try {
      const { data } = await axios.get(`${API}/public/invoice/${token}`);
      setInvoice(data);
      if (data.status === "paid") setPaymentStatus("paid");
    } catch (err) {
      setError("Invoice not found or link has expired.");
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = useCallback(async (sessionId, attempts) => {
    if (attempts >= 8) { setPaymentStatus("failed"); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === "paid") { setPaymentStatus("paid"); fetchInvoice(); return; }
      if (data.status === "expired") { setPaymentStatus("failed"); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  }, [token]);

  const handlePayStripe = async () => {
    setPaymentLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/invoice/${token}/checkout`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err.response?.data?.detail || "Payment failed. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePayRazorpay = async () => {
    setRazorpayLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/invoice/${token}/razorpay-checkout`);
      // Load Razorpay script dynamically
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        const options = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          order_id: data.order_id,
          name: invoice?.user?.business_name || "Invoice Payment",
          description: `Payment for ${data.invoice_number}`,
          handler: async (response) => {
            try {
              await axios.post(`${API}/public/invoice/${token}/razorpay-verify`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              setPaymentStatus("paid");
              fetchInvoice();
            } catch (e) {
              alert("Payment verification failed. Contact the sender.");
            }
          },
          theme: { color: "#1A3DFF" },
        };
        const rz = new window.Razorpay(options);
        rz.open();
      };
      document.body.appendChild(script);
    } catch (err) {
      alert(err.response?.data?.detail || "UPI payment setup failed.");
    } finally {
      setRazorpayLoading(false);
    }
  };

  const handlePayPalMe = () => {
    const username = invoice?.payment_config?.paypal_me;
    const amount = invoice?.total || 0;
    const currency = invoice?.currency || "USD";
    window.open(`https://paypal.me/${username}/${amount}${currency}`, "_blank");
  };

  const handleWise = () => {
    const email = invoice?.payment_config?.wise_email;
    window.open(`https://wise.com/pay/me/${email}`, "_blank");
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/public/invoice/${token}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to download PDF:", err);
    }
  };

  const formatCurrency = (amount, currency = "USD") =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount || 0);

  const getStatusBadge = (status) => {
    const classes = { draft: "badge badge-draft", sent: "badge badge-sent", paid: "badge badge-paid", overdue: "badge badge-overdue" };
    return <span className={classes[status] || classes.draft}>{status}</span>;
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><p className="text-[15px] text-[#6B7280]">Loading invoice...</p></div>;

  if (error || !invoice) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 bg-[#1A3DFF] flex items-center justify-center mx-auto mb-4"><span className="text-white font-bold text-[14px]">VB</span></div>
        <p className="text-[16px] font-semibold text-[#0A0A0A] mb-2">Invoice not found</p>
        <p className="text-[14px] text-[#6B7280]">{error || "This link may be invalid or expired."}</p>
      </div>
    </div>
  );

  const isPaid = invoice.status === "paid" || paymentStatus === "paid";
  const showBranding = invoice.user?.show_verobill_branding !== false;
  const pc = invoice.payment_config || {};
  const hasStripe = pc.stripe_enabled;
  const hasPayPal = !!pc.paypal_me;
  const hasRazorpay = pc.razorpay_enabled;
  const hasWise = !!pc.wise_email;
  const hasAnyPayment = hasStripe || hasPayPal || hasRazorpay || hasWise;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="public-invoice-page">
      {paymentStatus === "checking" && (
        <div className="bg-[#FEF3C7] border-b border-[#F59E0B] px-6 py-3 flex items-center justify-center gap-2" data-testid="payment-checking-banner">
          <Loader2 className="w-4 h-4 animate-spin text-[#D97706]" />
          <span className="text-[14px] font-medium text-[#92400E]">Verifying your payment...</span>
        </div>
      )}
      {paymentStatus === "paid" && (
        <div className="bg-[#ECFDF5] border-b border-[#10B981] px-6 py-3 flex items-center justify-center gap-2" data-testid="payment-success-banner">
          <CheckCircle className="w-4 h-4 text-[#059669]" />
          <span className="text-[14px] font-medium text-[#065F46]">Payment received. Thank you!</span>
        </div>
      )}

      {/* Header Bar */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40 no-print">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-3 sm:h-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
          <div className="flex items-center gap-3">
            <LogoMark size={26} />
            <span className="font-mono font-semibold text-[14px] text-[#0A0A0A]">{invoice.invoice_number}</span>
            {getStatusBadge(isPaid ? "paid" : invoice.status)}
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button onClick={handleDownloadPDF} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0A0A0A] text-white text-[13px] font-semibold hover:bg-[#374151] transition-colors" data-testid="public-download-pdf-btn">
              <Download className="w-4 h-4" /> PDF
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Payment Options Bar (below header, before invoice) */}
        {!isPaid && invoice.status !== "draft" && hasAnyPayment && (
          <div className="bg-white border border-[#E5E7EB] p-4 sm:p-5 mb-6" data-testid="payment-options">
            <p className="text-[13px] font-semibold text-[#0A0A0A] mb-3">Pay {formatCurrency(invoice.total, invoice.currency)}</p>
            <div className="flex flex-wrap gap-2">
              {hasStripe && (
                <button onClick={handlePayStripe} disabled={paymentLoading} className="flex items-center gap-2 px-4 py-2.5 bg-[#635BFF] text-white text-[13px] font-bold hover:bg-[#4B45C6] transition-colors disabled:opacity-50" data-testid="pay-stripe-btn">
                  {paymentLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Pay with Card
                </button>
              )}
              {hasPayPal && (
                <button onClick={handlePayPalMe} className="flex items-center gap-2 px-4 py-2.5 bg-[#0070BA] text-white text-[13px] font-bold hover:bg-[#005C9E] transition-colors" data-testid="pay-paypal-btn">
                  <span className="font-bold text-[11px]">PP</span> PayPal
                </button>
              )}
              {hasRazorpay && (
                <button onClick={handlePayRazorpay} disabled={razorpayLoading} className="flex items-center gap-2 px-4 py-2.5 bg-[#072654] text-white text-[13px] font-bold hover:bg-[#0A3A7A] transition-colors disabled:opacity-50" data-testid="pay-razorpay-btn">
                  {razorpayLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="font-bold text-[11px]">UPI</span>}
                  Pay with UPI
                </button>
              )}
              {hasWise && (
                <button onClick={handleWise} className="flex items-center gap-2 px-4 py-2.5 bg-[#9FE870] text-[#163300] text-[13px] font-bold hover:bg-[#8BD860] transition-colors" data-testid="pay-wise-btn">
                  <ExternalLink className="w-4 h-4" /> Wise Transfer
                </button>
              )}
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E5E7EB] shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-6 sm:p-12">
          {/* Invoice Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-6 sm:gap-0 mb-10 sm:mb-12">
            <div className="flex items-center gap-3">
              {invoice.user?.logo_url ? (
                <img src={`${BACKEND_URL}${invoice.user.logo_url}`} alt="Logo" className="w-10 h-10 object-contain flex-shrink-0" data-testid="invoice-logo" />
              ) : (invoice.user?.business_initials || invoice.user?.business_name) && (
                <div className="w-10 h-10 bg-[#1A3DFF] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-[14px]">
                    {invoice.user.business_initials || (invoice.user.business_name || "").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <h1 className="font-display text-[22px] tracking-[-0.03em] text-[#0A0A0A]">
                  {invoice.user?.business_name || invoice.user?.name || ""}
                </h1>
                {invoice.user?.email && <p className="text-[13px] text-[#6B7280]">{invoice.user.email}</p>}
                {invoice.user?.business_address && <p className="text-[12px] text-[#6B7280] whitespace-pre-line mt-0.5">{invoice.user.business_address}</p>}
              </div>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-1">Invoice</p>
              <p className="font-mono font-semibold text-[18px] text-[#0A0A0A]">{invoice.invoice_number}</p>
            </div>
          </div>

          {/* Bill To & Dates */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div>
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-2">Bill to</p>
              {invoice.client && (
                <>
                  <p className="font-semibold text-[16px] text-[#0A0A0A]">{invoice.client.name}</p>
                  {invoice.client.company && <p className="text-[14px] text-[#6B7280]">{invoice.client.company}</p>}
                  <p className="text-[14px] text-[#6B7280]">{invoice.client.email}</p>
                  {invoice.client.address && <p className="text-[14px] text-[#6B7280]">{invoice.client.address}</p>}
                  {invoice.client.city && <p className="text-[14px] text-[#6B7280]">{[invoice.client.city, invoice.client.country].filter(Boolean).join(", ")}</p>}
                </>
              )}
            </div>
            <div className="md:text-right">
              <div className="inline-block text-left">
                <div className="mb-3">
                  <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Issue date</p>
                  <p className="font-mono text-[14px] text-[#0A0A0A]">{invoice.issue_date}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Due date</p>
                  <p className="font-mono text-[14px] text-[#0A0A0A]">{invoice.due_date}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <table className="w-full min-w-[400px]">
              <thead>
                <tr className="border-b border-[#E5E7EB]">
                  <th className="text-left py-3 font-semibold text-[11px] text-[#9CA3AF] tracking-[0.05em] uppercase">Description</th>
                  <th className="text-right py-3 font-semibold text-[11px] text-[#9CA3AF] tracking-[0.05em] uppercase w-16 sm:w-20">Qty</th>
                  <th className="text-right py-3 font-semibold text-[11px] text-[#9CA3AF] tracking-[0.05em] uppercase w-24 sm:w-28">Rate</th>
                  <th className="text-right py-3 font-semibold text-[11px] text-[#9CA3AF] tracking-[0.05em] uppercase w-24 sm:w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index} className="border-b border-[#E5E7EB]">
                    <td className="py-4 text-[14px] text-[#0A0A0A]">{item.description}</td>
                    <td className="text-right py-4 font-mono text-[14px] text-[#0A0A0A]">{item.quantity}</td>
                    <td className="text-right py-4 font-mono text-[14px] text-[#0A0A0A]">{formatCurrency(item.rate, invoice.currency)}</td>
                    <td className="text-right py-4 font-mono font-semibold text-[14px] text-[#0A0A0A]">{formatCurrency(item.amount, invoice.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-full sm:w-64">
              <div className="flex justify-between py-2">
                <span className="text-[14px] text-[#6B7280]">Subtotal</span>
                <span className="font-mono text-[14px] text-[#0A0A0A]">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
              </div>
              {/* Multi-tax support */}
              {invoice.taxes && invoice.taxes.length > 0 ? (
                invoice.taxes.map((t, i) => (
                  <div key={i} className="flex justify-between py-2">
                    <span className="text-[14px] text-[#6B7280]">{t.label} ({t.rate}%)</span>
                    <span className="font-mono text-[14px] text-[#0A0A0A]">{formatCurrency(t.amount, invoice.currency)}</span>
                  </div>
                ))
              ) : invoice.tax_rate > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-[14px] text-[#6B7280]">Tax ({invoice.tax_rate}%)</span>
                  <span className="font-mono text-[14px] text-[#0A0A0A]">{formatCurrency(invoice.tax_amount, invoice.currency)}</span>
                </div>
              )}
              {invoice.discount > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-[14px] text-[#6B7280]">Discount</span>
                  <span className="font-mono text-[14px] text-[#0A0A0A]">-{formatCurrency(invoice.discount, invoice.currency)}</span>
                </div>
              )}
              <div className="flex justify-between py-3 border-t border-[#0A0A0A] mt-2">
                <span className="font-semibold text-[15px] text-[#0A0A0A]">Total</span>
                <span className="font-display text-[24px] tracking-[-0.02em] text-[#0A0A0A]">{formatCurrency(invoice.total, invoice.currency)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="border-t border-[#E5E7EB] pt-6 mb-6">
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-2">Notes</p>
              <p className="text-[14px] text-[#6B7280] whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}

          {/* Payment Details */}
          {invoice.payment_details && Object.keys(invoice.payment_details).length > 0 && (
            <div className="border-t border-[#E5E7EB] pt-6 mb-6">
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-3">Payment Details</p>
              {invoice.payment_details.bank_name && (
                <p className="text-[13px] text-[#6B7280] mb-1">
                  Bank: {invoice.payment_details.bank_name}
                  {invoice.payment_details.account_number && ` | Account: ${invoice.payment_details.account_number}`}
                  {invoice.payment_details.routing_number && ` | Sort Code: ${invoice.payment_details.routing_number}`}
                </p>
              )}
              {invoice.payment_details.paypal_email && <p className="text-[13px] text-[#6B7280] mb-1">PayPal: {invoice.payment_details.paypal_email}</p>}
              {invoice.payment_details.payment_notes && <p className="text-[13px] text-[#6B7280] mt-2">{invoice.payment_details.payment_notes}</p>}
            </div>
          )}

          {/* Tax Compliance */}
          {invoice.tax_compliance && (invoice.tax_compliance.tax_registration_number || (invoice.tax_compliance.compliance_notes && invoice.tax_compliance.compliance_notes.length > 0)) && (
            <div className="border-t border-[#E5E7EB] pt-6 mb-6" data-testid="tax-compliance-section">
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-2">Tax Information</p>
              {invoice.tax_compliance.tax_registration_number && (
                <p className="text-[13px] text-[#6B7280] mb-1 font-mono">
                  {invoice.tax_compliance.tax_id_label || "Tax ID"}: {invoice.tax_compliance.tax_registration_number}
                </p>
              )}
              {invoice.tax_compliance.compliance_notes?.map((note, i) => (
                <p key={i} className="text-[12px] text-[#9CA3AF] italic">{note}</p>
              ))}
            </div>
          )}

          {showBranding && (
            <div className="border-t border-[#E5E7EB] pt-6 text-center">
              <p className="text-[12px] text-[#9CA3AF]">Powered by VeroBill</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PublicInvoice;
